import { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { API } from '../config/api';
import {
  ListMusic, Plus, Check, Loader2, X, Music, Lock, Globe
} from 'lucide-react';

export function AddToPlaylistButton({ trackId, beatId, size = 'sm', variant = 'ghost', className = '' }) {
  const { isAuthenticated } = useAuth();
  const [open, setOpen] = useState(false);

  if (!isAuthenticated) return null;

  const itemId = trackId || beatId;
  const itemType = trackId ? 'track' : 'beat';

  return (
    <>
      <Button
        variant={variant}
        size={size}
        className={className}
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        data-testid={`add-to-playlist-btn-${itemId}`}
        title="Add to playlist"
      >
        <ListMusic className="w-4 h-4" />
      </Button>
      {open && (
        <AddToPlaylistModal
          trackId={trackId}
          beatId={beatId}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

function AddToPlaylistModal({ trackId, beatId, onClose }) {
  const { getAuthHeaders } = useAuth();
  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [showCreate, setShowCreate] = useState(false);

  const itemId = trackId || beatId;

  useEffect(() => {
    fetchPlaylists();
  }, []);

  const fetchPlaylists = async () => {
    try {
      const res = await fetch(`${API}/playlists?mine=true`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setPlaylists(data.playlists || []);
      }
    } catch {} finally {
      setLoading(false);
    }
  };

  const addToPlaylist = async (playlistId) => {
    setAdding(playlistId);
    try {
      const res = await fetch(`${API}/playlists/${playlistId}/tracks`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ track_id: itemId })
      });
      if (res.ok) {
        toast.success('Added to playlist!');
        onClose();
      } else {
        const err = await res.json().catch(() => ({}));
        if (err.detail?.includes('already')) {
          toast.info('Already in this playlist');
        } else {
          toast.error(err.detail || 'Failed to add');
        }
      }
    } catch {
      toast.error('Failed to add to playlist');
    } finally {
      setAdding(null);
    }
  };

  const createAndAdd = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const res = await fetch(`${API}/playlists`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newName.trim(), is_public: true })
      });
      if (res.ok) {
        const data = await res.json();
        // Now add the track
        await addToPlaylist(data.playlist_id);
      } else {
        toast.error('Failed to create playlist');
      }
    } catch {
      toast.error('Failed to create playlist');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-background/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <Card
        className="w-full max-w-sm bg-card border-border overflow-hidden"
        onClick={e => e.stopPropagation()}
        data-testid="add-to-playlist-modal"
      >
        <div className="p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-white flex items-center gap-2">
              <ListMusic className="w-4 h-4 text-purple-400" />Add to Playlist
            </h3>
            <Button variant="ghost" size="icon" onClick={onClose} className="w-7 h-7">
              <X className="w-4 h-4" />
            </Button>
          </div>

          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-purple-400" /></div>
          ) : (
            <div className="space-y-1 max-h-60 overflow-y-auto">
              {playlists.length === 0 && !showCreate && (
                <p className="text-sm text-muted-foreground text-center py-4">No playlists yet</p>
              )}
              {playlists.map(pl => (
                <button
                  key={pl.playlist_id}
                  onClick={() => addToPlaylist(pl.playlist_id)}
                  disabled={adding === pl.playlist_id}
                  className="w-full flex items-center gap-3 p-2.5 rounded-lg hover:bg-muted/25 transition-colors text-left"
                  data-testid={`playlist-option-${pl.playlist_id}`}
                >
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-600/20 to-pink-600/10 flex items-center justify-center flex-shrink-0">
                    {pl.cover_url ? (
                      <img src={pl.cover_url} alt="" className="w-full h-full object-cover rounded-lg" />
                    ) : (
                      <Music className="w-4 h-4 text-purple-400/60" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{pl.name}</p>
                    <p className="text-[10px] text-muted-foreground flex items-center gap-1">
                      {pl.is_public ? <Globe className="w-2.5 h-2.5" /> : <Lock className="w-2.5 h-2.5" />}
                      {pl.track_count} tracks
                    </p>
                  </div>
                  {adding === pl.playlist_id ? (
                    <Loader2 className="w-4 h-4 animate-spin text-purple-400" />
                  ) : (
                    <Plus className="w-4 h-4 text-muted-foreground" />
                  )}
                </button>
              ))}
            </div>
          )}

          {/* Create New */}
          {showCreate ? (
            <div className="mt-3 pt-3 border-t border-border">
              <div className="flex gap-2">
                <Input
                  data-testid="new-playlist-quick-name"
                  value={newName}
                  onChange={e => setNewName(e.target.value)}
                  placeholder="Playlist name"
                  className="bg-muted/50 border-border text-sm h-9"
                  autoFocus
                  onKeyDown={e => e.key === 'Enter' && createAndAdd()}
                />
                <Button
                  size="sm"
                  className="bg-purple-600 hover:bg-purple-700 h-9"
                  onClick={createAndAdd}
                  disabled={creating || !newName.trim()}
                  data-testid="quick-create-playlist-btn"
                >
                  {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                </Button>
              </div>
            </div>
          ) : (
            <Button
              variant="outline"
              size="sm"
              className="w-full mt-3 border-border text-foreground/80"
              onClick={() => setShowCreate(true)}
              data-testid="create-new-playlist-btn"
            >
              <Plus className="w-3.5 h-3.5 mr-1.5" />Create New Playlist
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
}
